import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';

type Permission = 'default' | 'granted' | 'denied';

const browserPermission = (): Permission => (
  typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'default'
);

const useBrowserPermission = (): [Permission, () => void] => {
  const [permission, setPermission] = useState<Permission>(browserPermission);
  const refresh = useCallback(() => setPermission(browserPermission()), []);

  // Site settings can change while the page is open; re-read when the tab comes back.
  useEffect(() => {
    window.addEventListener('focus', refresh);

    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);

  return [permission, refresh];
};

const BLOCKED_HINT = 'Notifications are blocked for this site. Allow them in your browser\'s site settings '
  + '(the icon left of the address), then reload.';

/** ``label`` is the visible text: only "Notify me" while off; on and blocked show just their bell. */
interface NotifyState { on: boolean; blocked: boolean; label: string; name: string; title: string; icon: string }

const notifyState = (permission: Permission, subscribed: boolean): NotifyState => {
  if (permission === 'denied') {
    return { on: false, blocked: true, label: '', name: 'Notifications blocked', title: BLOCKED_HINT,
      icon: 'fa-solid fa-bell-slash' };
  }
  if (subscribed) {
    return { on: true, blocked: false, label: '', name: 'Notifications on', icon: 'fa-solid fa-bell',
      title: 'Notifications on: you\'ll hear when work needs you or is done. Click to turn off.' };
  }

  return { on: false, blocked: false, label: 'Notify me', name: 'Notify me', icon: 'fa-regular fa-bell',
    title: 'Get a browser notification when work needs you or is done.' };
};

/** Turn push on (asking the browser if needed) or off; refreshes the permission afterwards. */
const useNotifyToggle = (state: NotifyState, refreshPermission: () => void) => {
  const { subscribeToPush, unsubscribePush } = usePushNotifications();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (state.blocked || busy) return;
    setBusy(true);
    try {
      await (state.on ? unsubscribePush() : subscribeToPush());
    } finally {
      refreshPermission();
      setBusy(false);
    }
  };

  return { busy, toggle };
};

/**
 * Whether this browser tells the user when their planning work needs them or is done, and the switch for it.
 * Hidden where push is unsupported and while an admin is viewing as a user (the browser is the admin's).
 */
export const NotifyMeButton: React.FC = () => {
  const { isImpersonating } = useAuth();
  const { isSupported, isSubscribed } = usePushNotifications();
  const [permission, refreshPermission] = useBrowserPermission();
  const state = notifyState(permission, isSubscribed);
  const { busy, toggle } = useNotifyToggle(state, refreshPermission);

  if (!isSupported || isImpersonating) return null;

  return (
    <button type="button" title={state.title} aria-label={state.name} onClick={() => void toggle()}
      className={`active-tasks-dock__notify${state.on ? ' is-on' : ''}${state.blocked ? ' is-blocked' : ''}`}
      aria-pressed={state.on} aria-disabled={state.blocked || undefined} aria-busy={busy || undefined}>
      <i className={state.icon} aria-hidden="true" />
      {state.label && <span>{state.label}</span>}
    </button>
  );
};
